import React, { useEffect, useMemo, useState } from 'react';
import { logUsage } from '../../../services/eventLog';
import type { Column } from '../utils/transformLoad';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { loadStdDictionary, StdDictionary, EMPTY_DICTIONARY } from './stdDictionary';
import {
  suggestMapping, mergeAiPairs, AiPair, Suggestion, SuggestResult, applyOp, describeOp, opToExpr,
  evidenceLabel, confidenceLabel,
} from './suggestMapping';
import { useAssistantEnabled } from '../../../hooks/useAssistantEnabled';
import { useTranslation } from 'react-i18next';
import * as assistant from '../../../services/assistantClient';

interface Props {
  sourceColumns: Column[];
  targetColumns: Column[];
  /** 소스 샘플 행 — 없으면 값 기반 판정(날짜 포맷·길이 초과)이 빠진다 */
  sourceRows?: Array<Record<string, unknown>>;
  /** 선택한 제안을 매핑에 반영 */
  onApply: (items: Array<{ target: string; source: string; expr: string }>) => void;
  onClose: () => void;
}

const CONF_STYLE: Record<string, string> = {
  confirmed: 'bg-green-100 text-green-700',
  likely: 'bg-blue-100 text-blue-700',
  weak: 'bg-amber-100 text-amber-700',
};

/**
 * 매핑 제안 — 이름·표준용어로 후보를 찾고, 타입·길이·샘플 값으로 검증해 변환까지 제안한다.
 * 제안을 그대로 믿게 하지 않고 **샘플에 적용한 결과를 함께 보여 주는 것**이 이 화면의 핵심이다.
 */
const SuggestionPanel: React.FC<Props> = ({ sourceColumns, targetColumns, sourceRows, onApply, onClose }) => {
  const assistantOn = useAssistantEnabled();
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const [dict, setDict] = useState<StdDictionary>(EMPTY_DICTIONARY);
  const [dictLoading, setDictLoading] = useState(true);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  // AI 보조 — 규칙이 못 붙인 항목만 넘겨 의미로 짝을 찾아 본다
  const [aiPairs, setAiPairs] = useState<AiPair[] | null>(null);
  const [aiNotes, setAiNotes] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState('');

  useEffect(() => {
    let alive = true;
    loadStdDictionary().then((d) => { if (alive) { setDict(d); setDictLoading(false); } });
    return () => { alive = false; };
  }, []);

  const input = useMemo(
    () => ({ sourceColumns, targetColumns, sourceRows, dictionary: dict }),
    [sourceColumns, targetColumns, sourceRows, dict],
  );
  const base: SuggestResult = useMemo(() => suggestMapping(input), [input]);
  // 규칙 결과가 바탕이고 AI 는 그 위에 덧붙는다 — 규칙이 붙인 짝은 AI 가 바꾸지 못한다
  const result: SuggestResult = useMemo(
    () => (aiPairs ? mergeAiPairs(input, base, aiPairs) : base),
    [input, base, aiPairs],
  );

  /** 표본은 앞 3행만 보낸다 — 형태를 판단하는 데 그 이상은 필요 없고, 값이 밖으로 나가는 양을 줄인다 */
  const askAi = async () => {
    setAiBusy(true); setAiError(''); setAiNotes('');
    try {
      const colInfo = (names: string[], cols: Column[], withSamples: boolean) =>
        names.map((n) => {
          const c = cols.find((x) => x.name === n);
          const samples = withSamples
            ? (sourceRows || []).slice(0, 3).map((r) => r[n]).filter((v) => v !== undefined && v !== null && v !== '')
            : [];
          return { name: n, dataType: c?.dataType || '', ...(samples.length ? { samples } : {}) };
        });
      // 계정 화면에서 고른 제공자·모델을 그대로 쓴다(대화와 같은 계정으로 돈다)
      const pref = (k: string, d: string) => { try { return localStorage.getItem(k) || d; } catch { return d; } };
      const r = await assistant.runTask<{ pairs: AiPair[]; notes?: string }>('suggest_mapping', {
        matched: base.suggestions.map((x) => ({ target: x.target, source: x.source })),
        unmatchedTargets: colInfo(base.unmatchedTargets, targetColumns, false),
        unusedSources: colInfo(base.unusedSources, sourceColumns, true),
      }, {
        provider: pref('openflow.assistant.provider', 'claude'),
        model: pref('openflow.assistant.model', 'default'),
      });
      setAiPairs(r.result.pairs || []);
      setAiNotes(r.result.notes || '');
    } catch (e: any) {
      setAiError(e?.message || t('property:suggest.aiFailed'));
    } finally {
      setAiBusy(false);
    }
  };

  // 확정·추정은 기본 선택, 주의(순서 폴백 등)는 사용자가 직접 고르게 둔다
  useEffect(() => {
    const init: Record<string, boolean> = {};
    result.suggestions.forEach((s) => {
      // 주의 등급(순서 폴백)과 AI 제안은 사용자가 직접 고르게 둔다 — 눈으로 확인해야 하는 것들이다
      init[s.target] = s.confidence !== 'weak' && !s.evidence.includes('ai');
    });
    setChecked(init);
  }, [result]);

  const selected = result.suggestions.filter((s) => checked[s.target]);
  const apply = () => {
    logUsage('mapping.apply', { detail: { applied: selected.length, suggested: result.suggestions.length } });
    onApply(selected.map((s) => ({ target: s.target, source: s.source, expr: opToExpr(s.op, s.source) })));
  };

  const previewRows = (sourceRows || []).slice(0, 5);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-[900px] max-w-[95vw] h-[80vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('property:suggest.title')}</div>
            <div className="text-[0.6875rem] text-gray-500">
              {t('property:suggest.intro')}
              {dictLoading ? t('property:suggest.dictLoading') : dict.loaded ? t('property:suggest.dictUsed', { name: dict.stdNm }) : t('property:suggest.dictNone')}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* 규칙으로 못 붙인 것이 남았을 때만 의미가 있다 */}
            {assistantOn && base.unmatchedTargets.length > 0 && (
              <button
                className="px-2 py-1 text-xs rounded border border-violet-400 text-violet-700 hover:bg-violet-50 disabled:opacity-50"
                disabled={aiBusy || aiPairs !== null}
                onClick={askAi}
                title={t('property:suggest.aiTitle')}
              >
                {aiBusy ? t('property:suggest.aiBusy') : aiPairs !== null ? t('property:suggest.aiDone') : t('property:suggest.aiButton')}
              </button>
            )}
            <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto p-3">
          {aiError && (
            <div className="mb-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-[0.6875rem] text-red-700">{aiError}</div>
          )}
          {aiPairs !== null && (
            <div className="mb-2 rounded bg-violet-50 px-2 py-1 text-[0.6875rem] text-violet-800">
              {t('property:suggest.aiResult', { n: aiPairs.length })}
              {aiNotes && <div className="mt-0.5 text-violet-700">{aiNotes}</div>}
            </div>
          )}
          {result.suggestions.length === 0 ? (
            <div className="text-xs text-gray-500">
              {t('property:suggest.none')}
            </div>
          ) : (
            <table className="text-xs w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1 w-8"></th>
                  <th className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-200">{t('property:suggest.colTarget')}</th>
                  <th className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-200">{t('property:suggest.colSource')}</th>
                  <th className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-200">{t('property:suggest.colEvidence')}</th>
                  <th className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-200">{t('property:suggest.colTransform')}</th>
                  <th className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-200">{t('property:suggest.colCheck')}</th>
                </tr>
              </thead>
              <tbody>
                {result.suggestions.map((s: Suggestion) => (
                  <tr key={s.target} className="hover:bg-gray-50">
                    <td className="px-2 py-1 border-b border-gray-100">
                      <input type="checkbox" checked={!!checked[s.target]}
                        onChange={(e) => setChecked((p) => ({ ...p, [s.target]: e.target.checked }))} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 font-medium text-gray-800">{s.target}</td>
                    <td className="px-2 py-1 border-b border-gray-100 text-gray-700">{s.source}</td>
                    <td className="px-2 py-1 border-b border-gray-100">
                      <span className={`px-1 rounded ${CONF_STYLE[s.confidence]}`}>{confidenceLabel(s.confidence)}</span>
                      <span className="ml-1 text-[0.625rem] text-gray-500">{s.evidence.map((e) => evidenceLabel(e)).join('·')}</span>
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 text-gray-600">{describeOp(s.op)}</td>
                    <td className="px-2 py-1 border-b border-gray-100 text-amber-700">{s.warnings.join(' / ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {(result.unmatchedTargets.length > 0 || result.unusedSources.length > 0) && (
            <div className="mt-2 text-[0.6875rem] text-gray-600 space-y-0.5">
              {result.unmatchedTargets.length > 0 && (
                <div>{t('property:suggest.unmatched')}<b>{result.unmatchedTargets.join(', ')}</b></div>
              )}
              {result.unusedSources.length > 0 && (
                <div>{t('property:suggest.unused')}<b>{result.unusedSources.join(', ')}</b></div>
              )}
            </div>
          )}

          {/* 샘플에 적용한 결과 — 제안이 맞는지 눈으로 판단하는 통로 */}
          <div className="mt-3">
            <div className="text-xs font-semibold text-gray-600 mb-1">
              {t('property:suggest.preview')}{previewRows.length ? t('property:suggest.previewRows', { n: previewRows.length }) : ''}
            </div>
            {previewRows.length === 0 ? (
              <div className="text-[0.6875rem] text-gray-400">{t('property:suggest.noSamples')}</div>
            ) : (
              <div className="overflow-auto border border-gray-200 rounded max-h-48">
                <table className="text-xs w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>{selected.map((s) => <th key={s.target} className="px-1.5 py-0.5 text-left font-medium text-gray-700 border-b border-gray-200 whitespace-nowrap">{s.target}</th>)}</tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => (
                      <tr key={i}>
                        {selected.map((s) => (
                          <td key={s.target} className="px-1.5 py-0.5 border-b border-gray-100 whitespace-nowrap">
                            {applyOp(s.op, row[s.source])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="px-4 py-2.5 border-t border-gray-200 flex items-center gap-2">
          <span className="text-[0.6875rem] text-gray-500 mr-auto">{t('property:suggest.selected', { n: selected.length, total: result.suggestions.length })}</span>
          <button className="px-3 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={onClose}>{t('common:ui.cancel')}</button>
          <button className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            disabled={selected.length === 0} onClick={apply}>{t('property:suggest.applySelected', { n: selected.length })}</button>
        </div>
      </div>
    </div>
  );
};

export default SuggestionPanel;
